import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { ArrowDown, ArrowUp, ChevronDown, ChevronRight, Eye, Plus, ShieldAlert, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { KorektaMpk } from '@/components/KorektaMpk';
import { PodgladZmian } from '@/components/PodgladZmian';
import { ZapytaniaBazy } from '@/components/ZapytaniaBazy';
import { StatusZmiany } from '@/components/StatusZmiany';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { Tabs } from '@/components/ui/tabs';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import {
  fetchCatalog,
  fetchCurrent,
  fetchNextKey,
  fetchOperations,
  validateCell,
  type AuthoringOperation,
  type AuthoringTable,
  type CellValue,
  type DraftChange,
} from '@/lib/authoring-api';

/**
 * Edytor config-down.
 *
 * Najbardziej niebezpieczna powierzchnia w portalu: pozwala złożyć zapis do bazy klienta wprost.
 * Dlatego UI jest zbudowane wokół trzech zasad, nie wokół wygody:
 *  1. NIE MA pola na surowy JSON — pola generujemy z deklaracji kolumn, więc nie da się wpisać
 *     nazwy kolumny, której nie potwierdziliśmy.
 *  2. Zapis wymaga PODGLĄDU tej samej paczki (token) i potwierdzenia każdego ostrzeżenia.
 *  3. Słownictwo: „zakolejkowano", nigdy „wysłano"/„zastosowano" — v1 nie ma agenta, więc nikt
 *     tego nie wykonuje na automacie.
 */

// ─────────────────────────── kreator jednej zmiany ───────────────────────────

type CellState = { raw: string; enabled: boolean };

function ChangeBuilder({
  table,
  groupId,
  onAdd,
}: {
  table: AuthoringTable;
  groupId: string;
  onAdd: (change: DraftChange) => void;
}) {
  const { t } = useI18n();
  const [action, setAction] = useState<DraftChange['action']>(table.actions[0]!);
  const [keyCells, setKeyCells] = useState<Record<string, string>>({});
  const [cols, setCols] = useState<Record<string, CellState>>({});

  // zmiana tabeli albo akcji zmienia cały zestaw pól — czyścimy, żeby nie zostały wartości
  // złożone dla innej tabeli (ten sam powód, dla którego modale remountujemy przez `key`)
  useEffect(() => {
    setKeyCells({});
    setCols({});
  }, [table.name, action]);

  const nextKey = useQuery({
    queryKey: ['authoring', 'next-key', groupId, table.name],
    queryFn: () => fetchNextKey(groupId, table.name),
    enabled: action === 'I' && table.hasGenerator,
  });

  const keyComplete = table.key.every((k) => (keyCells[k.name] ?? '').trim() !== '');
  const pkForLookup = useMemo(() => {
    if (!keyComplete) return null;
    const out: Record<string, string | number> = {};
    for (const k of table.key) {
      const { value } = validateCell(k.type, keyCells[k.name] ?? '');
      if (value === null || typeof value === 'boolean') return null;
      out[k.name] = value;
    }
    return out;
  }, [keyComplete, keyCells, table.key]);

  // „co portal ma dziś" — jedyny sposób, żeby patch nie był pisany na wyczucie
  const current = useQuery({
    queryKey: ['authoring', 'current', groupId, table.name, JSON.stringify(pkForLookup)],
    queryFn: () => fetchCurrent(groupId, table.name, pkForLookup!),
    enabled: pkForLookup !== null,
  });

  const keyErrors = table.key.map((k) => ({
    name: k.name,
    error: (keyCells[k.name] ?? '').trim() === '' ? null : validateCell(k.type, keyCells[k.name] ?? '').error,
  }));

  const chosen = Object.entries(cols).filter(([, c]) => c.enabled);
  const colErrors = chosen.map(([name, c]) => {
    const col = table.columns.find((x) => x.name === name)!;
    return { name, error: validateCell(col.type, c.raw).error };
  });

  const canAdd =
    keyComplete &&
    keyErrors.every((k) => !k.error) &&
    colErrors.every((c) => !c.error) &&
    (action === 'P' ? chosen.length > 0 : true);

  const add = () => {
    if (!pkForLookup) return;
    /** Usunięcie wskazuje wiersz samym kluczem — bez danych, jak w module, który usuwa ten sam liść. */
    if (action === 'D') {
      onAdd({ tableName: table.name, action, pk: pkForLookup });
      setKeyCells({});
      return;
    }
    const payload: Record<string, CellValue> = {};
    // przy I klucz jedzie także w danych wiersza — tak wygląda pełny wiersz w SPS
    if (action === 'I') for (const [name, v] of Object.entries(pkForLookup)) payload[name] = v;
    for (const [name, c] of chosen) {
      const col = table.columns.find((x) => x.name === name)!;
      payload[name] = validateCell(col.type, c.raw).value;
    }
    onAdd({ tableName: table.name, action, pk: pkForLookup, payload });
    setKeyCells({});
    setCols({});
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <label className="grid gap-1 text-xs">
          {t('cd.action')}
          <Select value={action} onChange={(e) => setAction(e.target.value as DraftChange['action'])} className="w-56">
            {table.actions.map((a) => (
              <option key={a} value={a}>
                {a === 'I' ? t('cd.actionI') : a === 'D' ? t('cd.actionD') : t('cd.actionP')}
              </option>
            ))}
          </Select>
        </label>
        {action === 'I' && table.hasGenerator && nextKey.data && (
          <span className="pb-2 text-xs text-muted-foreground">
            {t('cd.nextKeyHint')
              .replace('{next}', String(nextKey.data.next))
              .replace('{max}', String(nextKey.data.max))}
          </span>
        )}
      </div>

      {/* klucz naturalny — nazwy pól pochodzą z deklaracji, nie z wpisywania */}
      <fieldset className="rounded-md border border-border p-3">
        <legend className="px-1 text-xs text-muted-foreground">{t('cd.naturalKey')}</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {table.key.map((k) => {
            const err = keyErrors.find((e) => e.name === k.name)?.error ?? null;
            return (
              <label key={k.name} className="grid gap-1 text-xs">
                <span>
                  <span className="font-mono">{k.name}</span> — {k.label}
                </span>
                <Input
                  value={keyCells[k.name] ?? ''}
                  onChange={(e) => setKeyCells((s) => ({ ...s, [k.name]: e.target.value }))}
                  inputMode={k.type?.kind === 'int' || k.type?.kind === 'enum' ? 'numeric' : 'text'}
                  aria-label={k.name}
                  aria-invalid={Boolean(err)}
                />
                {err && <span className="text-destructive">{err}</span>}
              </label>
            );
          })}
        </div>

        {pkForLookup && (
          <div className="mt-3 rounded border border-border bg-muted/40 p-2 text-xs">
            {current.isPending ? (
              <span className="text-muted-foreground">{t('common.loading')}</span>
            ) : current.data?.current ? (
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                <span className="font-medium">{t('cd.portalHasNow')}</span>
                {Object.entries(current.data.current).map(([k, v]) => (
                  <span key={k} className="tabular-nums">
                    <span className="font-mono text-muted-foreground">{k}</span>={String(v ?? '—')}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-muted-foreground">{t('cd.portalDoesNotKnow')}</span>
            )}
          </div>
        )}
      </fieldset>

      {/* kolumny zapisywalne — wybierane checkboxem z listy zadeklarowanej, bez pola „inna kolumna" */}
      {action === 'D' ? (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {t('cd.deleteByKey')}
        </p>
      ) : table.columns.length > 0 ? (
        <fieldset className="rounded-md border border-border p-3">
          <legend className="px-1 text-xs text-muted-foreground">
            {action === 'I' ? t('cd.rowData') : t('cd.columnsToPatch')}
          </legend>
          <div className="space-y-2">
            {table.columns.map((col) => {
              const state = cols[col.name] ?? { raw: '', enabled: false };
              const err = state.enabled ? validateCell(col.type, state.raw).error : null;
              return (
                <div key={col.name} className="grid gap-2 sm:grid-cols-[1.2fr_2fr] sm:items-start">
                  <label className="flex items-center gap-2 text-xs">
                    <input
                      type="checkbox"
                      checked={state.enabled}
                      disabled={!col.editable}
                      onChange={(e) =>
                        setCols((s) => ({ ...s, [col.name]: { raw: state.raw, enabled: e.target.checked } }))
                      }
                    />
                    <span>
                      <span className="font-mono">{col.name}</span> — {col.label}
                    </span>
                  </label>
                  <div className="grid gap-1">
                    <Input
                      value={state.raw}
                      disabled={!state.enabled}
                      onChange={(e) => setCols((s) => ({ ...s, [col.name]: { raw: e.target.value, enabled: true } }))}
                      placeholder={col.type ? describeType(col.type) : t('cd.noTypeDeclared')}
                      aria-label={col.name}
                      aria-invalid={Boolean(err)}
                    />
                    {err && <span className="text-xs text-destructive">{err}</span>}
                  </div>
                </div>
              );
            })}
          </div>
        </fieldset>
      ) : (
        <p className="rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
          {t('cd.keyOnlyTable')}
        </p>
      )}

      <Button type="button" disabled={!canAdd} onClick={add}>
        <Plus />
        {t('cd.addToBatch')}
      </Button>
    </div>
  );
}

/** Podpowiedź pod polem. ⚠️ Rodzaje muszą być WYCZERPANE — `tsc` to wymusza, bo unia jest domknięta. */
function describeType(type: NonNullable<AuthoringTable['columns'][number]['type']>): string {
  if (type.kind === 'int') return `${type.min}–${type.max}`;
  if (type.kind === 'enum') return type.values.join(' / ');
  if (type.kind === 'decimal') return 'np. 19.90';
  if (type.kind === 'float') return `${type.min}–${type.max} (ułamki dozwolone)`;
  if (type.kind === 'wallclock') return 'RRRR-MM-DD lub RRRR-MM-DDTHH:MM:SS — bez strefy';
  return `${type.minLen}–${type.maxLen} znaków`;
}

// ─────────────────────────── strona ──────────────────────────────────────────

export function Authoring() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { groupId, island } = useIslands();

  /**
   * ⚠️ ZAKŁADKA „ZAPYTANIA" PIERWSZA I DOMYŚLNA (28.09.2026): odczyt jak w SSMS jest tym, od czego
   * zaczyna się każda praca na bazie — dopiero wiedząc, co w niej stoi, składa się zmianę.
   */
  const [zakladka, setZakladka] = useState<'zapytania' | 'zmiany'>('zapytania');
  const [tableName, setTableName] = useState('');
  const [batch, setBatch] = useState<DraftChange[]>([]);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  const [state, setState] = useState<DataTableState>({ search: '', page: 1, pageSize: 25, sort: 'createdAt', dir: 'desc' });

  const catalog = useQuery({
    queryKey: ['authoring', 'catalog', groupId],
    queryFn: () => fetchCatalog(groupId!),
    enabled: Boolean(groupId),
  });

  // PRZEŁĄCZENIE WYSPY CZYŚCI SZKIC. Bez tego dałoby się złożyć paczkę dla wyspy A,
  // przestawić selektor w nagłówku i wysłać ją do bazy wyspy B.
  useEffect(() => {
    setBatch([]);
    setTableName('');
    setPreviewOpen(false);
  }, [groupId]);

  const params = useMemo(() => ({ ...state, groupId: groupId ?? '' }), [state, groupId]);
  const operations = useQuery({
    queryKey: ['authoring', 'operations', params],
    queryFn: () => fetchOperations(params),
    enabled: Boolean(groupId),
  });

  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);

  /**
   * Eksport CALEGO rejestru, nie widocznej strony.
   *
   * To jedyna sciezka audytu operacji z najgrozniejszej powierzchni portalu — eksport obejmujacy
   * po cichu 25 wierszy z kilkuset dawalby plik wygladajacy na kompletny. Limit jest JAWNY:
   * po jego przekroczeniu DataTable zglasza obciecie toastem.
   */
  const fetchAllOperations = useCallback(async (): Promise<AuthoringOperation[]> => {
    if (!groupId) return [];
    const out: AuthoringOperation[] = [];
    for (let page = 1; ; page += 1) {
      const chunk = await fetchOperations({ ...params, page, pageSize: 200 });
      out.push(...chunk.rows);
      if (out.length >= chunk.total || chunk.rows.length === 0) break;
    }
    return out;
  }, [params, groupId]);
  const table = catalog.data?.tables.find((x) => x.name === tableName) ?? null;

  const columns = useMemo<ColumnDef<AuthoringOperation>[]>(
    () => [
      {
        id: 'createdAt',
        header: t('cd.when'),
        meta: { exportValue: (r) => r.createdAt },
        cell: ({ row }) => (
          <span className="whitespace-nowrap text-xs tabular-nums">
            {new Date(row.original.createdAt).toLocaleString('pl-PL')}
          </span>
        ),
      },
      {
        id: 'title',
        header: t('cd.opTitle'),
        meta: { exportValue: (r) => r.title },
        cell: ({ row }) => <span className="text-sm">{row.original.title}</span>,
      },
      {
        id: 'changes',
        header: t('cd.opChanges'),
        enableSorting: false,
        meta: { exportValue: (r) => r.changes.map((c) => `${c.tableName} ${c.action} ${JSON.stringify(c.pk)}`).join(' | ') },
        cell: ({ row }) => (
          <div className="space-y-0.5">
            {row.original.changes.map((c) => (
              <div key={c.seq} className="font-mono text-xs">
                <span className="text-muted-foreground">#{c.seq}</span> {c.tableName} {c.action}{' '}
                {JSON.stringify(c.pk)}
              </div>
            ))}
          </div>
        ),
      },
      {
        id: 'authorNote',
        header: t('cd.authorNote'),
        enableSorting: false,
        meta: { exportValue: (r) => r.authorNote ?? '' },
        cell: ({ row }) => <span className="text-xs">{row.original.authorNote ?? '—'}</span>,
      },
      {
        id: 'author',
        header: t('cd.author'),
        enableSorting: false,
        meta: { exportValue: (r) => r.createdBy?.email ?? '' },
        cell: ({ row }) => <span className="text-xs">{row.original.createdBy?.email ?? '—'}</span>,
      },
      {
        id: 'status',
        header: t('cd.status'),
        enableSorting: false,
        meta: { exportValue: (r) => (r.error ? `${r.status}: ${r.error}` : r.status) },
        /**
         * ⚠️ PRAWDZIWY STAN, NIE STAŁA. Do 2026-08-25 stała tu plakietka „w kolejce" rysowana
         * na sztywno, bez patrzenia na `status` — więc operacja wykonana albo odrzucona przez
         * agenta wyglądała tu na zamrożoną, a `exportValue` z tego samego wiersza wypisywał
         * do CSV stan prawdziwy. Powód odmowy pokazujemy obok, bo `FAILED` zwykle znaczy
         * „ktoś zmienił ten wiersz w SPSUI", czyli coś do naprawienia, a nie awarię.
         */
        cell: ({ row }) => (
          <div className="space-y-1">
            <StatusZmiany status={row.original.status} />
            {row.original.error && <p className="max-w-[28ch] text-xs text-[var(--destructive)]">{row.original.error}</p>}
          </div>
        ),
      },
    ],
    [t],
  );

  if (!groupId) return <p className="text-sm text-muted-foreground">{t('islands.none')}</p>;

  return (
    <div className="space-y-6">
      {/* baner stały, niezamykalny — v1 nie ma agenta */}
      <div className="rounded-lg border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-4 text-sm">
        <p className="flex items-center gap-2 font-medium">
          <ShieldAlert className="size-4" />
          {t('cd.bannerTitle')}
        </p>
        <p className="mt-1 text-xs">{t('cd.bannerBody')}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          {t('cd.bannerIsland')
            .replace('{client}', island?.clientName ?? '')
            .replace('{island}', island?.name ?? '')}
        </p>
      </div>

      <Tabs
        zakladki={[
          { id: 'zapytania', etykieta: t('baza.zakladka') },
          { id: 'zmiany', etykieta: t('baza.zakladkaZmiany') },
        ]}
        wybrana={zakladka}
        onWybor={setZakladka}
      />

      {zakladka === 'zapytania' && <ZapytaniaBazy key={groupId} groupId={groupId} />}

      {zakladka === 'zmiany' && (
      <>
      <section className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-semibold">{t('cd.builderTitle')}</h2>
        <label className="mt-3 grid max-w-xl gap-1 text-xs">
          {t('cd.table')}
          <Select value={tableName} onChange={(e) => setTableName(e.target.value)}>
            <option value="">{t('cd.chooseTable')}</option>
            {catalog.data?.tables.map((tb) => (
              <option key={tb.name} value={tb.name}>
                {tb.name} — {tb.label} ({tb.actions.join('/')})
              </option>
            ))}
          </Select>
        </label>

        {table && (
          <div className="mt-4 space-y-3">
            <div className="flex flex-wrap gap-2">
              <Badge variant={table.mappingStatus === 'assumed' ? 'warning' : 'success'} title={table.mappingNote ?? ''}>
                {table.mappingStatus === 'assumed' ? t('cd.mappingAssumed') : t('cd.mappingConfirmed')}
              </Badge>
              {table.owner.kind === 'module' && (
                <Badge variant="neutral" title={t('cd.ownedHint')}>
                  {t('cd.ownedBy').replace('{module}', table.owner.label)}
                </Badge>
              )}
            </div>
            {table.mappingStatus === 'assumed' && table.mappingNote && (
              <p className="rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-xs">
                {table.mappingNote}
              </p>
            )}
            {/* remount przy zmianie tabeli — inaczej zostają wartości złożone dla innej tabeli */}
            <ChangeBuilder
              key={table.name}
              table={table}
              groupId={groupId}
              onAdd={(c) => setBatch((b) => [...b, c])}
            />
          </div>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h2 className="text-sm font-semibold">
            {t('cd.batchTitle')} ({batch.length})
          </h2>
          <div className="flex gap-2">
            <Button type="button" variant="outline" disabled={batch.length === 0} onClick={() => setBatch([])}>
              {t('cd.clearBatch')}
            </Button>
            <Button type="button" disabled={batch.length === 0} onClick={() => setPreviewOpen(true)}>
              <Eye />
              {t('cd.preview')}
            </Button>
          </div>
        </div>

        {batch.length === 0 ? (
          <p className="mt-3 text-xs text-muted-foreground">{t('cd.batchEmpty')}</p>
        ) : (
          <>
            <p className="mt-2 text-xs text-muted-foreground">{t('cd.batchOrderHint')}</p>
            <ol className="mt-2 space-y-2">
              {batch.map((c, i) => (
                <li key={`${c.tableName}-${JSON.stringify(c.pk)}-${i}`} className="flex items-start gap-2 rounded-md border border-border p-2">
                  <span className="pt-1 font-mono text-xs text-muted-foreground">{i + 1}.</span>
                  <div className="min-w-0 flex-1 font-mono text-xs">
                    <div>
                      {c.tableName} <strong>{c.action}</strong> {JSON.stringify(c.pk)}
                    </div>
                    {c.payload && <div className="text-muted-foreground">{JSON.stringify(c.payload)}</div>}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('cd.moveUp')}
                      aria-label={`${t('cd.moveUp')} ${i + 1}`}
                      disabled={i === 0}
                      onClick={() =>
                        setBatch((b) => {
                          const next = [...b];
                          [next[i - 1], next[i]] = [next[i]!, next[i - 1]!];
                          return next;
                        })
                      }
                    >
                      <ArrowUp />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('cd.moveDown')}
                      aria-label={`${t('cd.moveDown')} ${i + 1}`}
                      disabled={i === batch.length - 1}
                      onClick={() =>
                        setBatch((b) => {
                          const next = [...b];
                          [next[i], next[i + 1]] = [next[i + 1]!, next[i]!];
                          return next;
                        })
                      }
                    >
                      <ArrowDown />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      title={t('cd.removeFromBatch')}
                      aria-label={`${t('cd.removeFromBatch')} ${i + 1}`}
                      onClick={() => setBatch((b) => b.filter((_, idx) => idx !== i))}
                    >
                      <Trash2 />
                    </Button>
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
      </section>

      {/*
        KOREKTA MPK W HISTORII WYDAŃ — tu, bo tak zamówione („wrzucić w edytor config-down").
        ⚠️ `key` po wyspie: przełączenie wyspy czyści formularz, jak szkic paczki wyżej.
      */}
      <KorektaMpk key={groupId} groupId={groupId} />

      {/* czego edytor NIE otwiera — wystawione celowo, żeby brak nie wyglądał na przeoczenie */}
      <section className="rounded-lg border border-border bg-card">
        <button
          type="button"
          className="flex w-full items-center gap-2 p-4 text-left text-sm font-semibold"
          aria-expanded={showClosed}
          onClick={() => setShowClosed((v) => !v)}
        >
          {showClosed ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />}
          {t('cd.closedTitle')
            .replace('{unavailable}', String(catalog.data?.unavailable.length ?? 0))
            .replace('{forbidden}', String(catalog.data?.forbidden.length ?? 0))}
        </button>
        {showClosed && (
          <div className="space-y-4 border-t border-border p-4 text-xs">
            <div>
              <p className="font-medium">{t('cd.notOfferedActions')}</p>
              <ul className="mt-1 space-y-1">
                {catalog.data?.actionsNotOffered.map((a) => (
                  <li key={a.action}>
                    <span className="font-mono">{a.action}</span> — {a.reason}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-medium">{t('cd.forbiddenTables')}</p>
              <ul className="mt-1 space-y-1">
                {catalog.data?.forbidden.map((f) => (
                  <li key={f.table}>
                    <span className="font-mono">{f.table}</span> — {f.reason}
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="font-medium">{t('cd.unavailableTables')}</p>
              <ul className="mt-1 grid gap-1 sm:grid-cols-2">
                {catalog.data?.unavailable.map((u) => (
                  <li key={u.table} className="font-mono text-muted-foreground">
                    {u.table}
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-muted-foreground">{catalog.data?.unavailable[0]?.reason}</p>
            </div>
          </div>
        )}
      </section>

      <DataTable<AuthoringOperation>
        title={t('cd.registryTitle')}
        columns={columns}
        data={operations.data?.rows ?? []}
        total={operations.data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={operations.isPending}
        isFetching={operations.isFetching}
        onRefresh={() => void operations.refetch()}
        rowKey={(r) => r.id}
        exportFileName={`config-down-${island?.code ?? 'wyspa'}`}
        fetchAllRows={fetchAllOperations}
        reportKey="authoring"
        reportGroupId={groupId}
        emptyText={t('cd.registryEmpty')}
      />
      </>
      )}

      {previewOpen && (
        <PodgladZmian
          groupId={groupId}
          changes={batch}
          onClose={() => setPreviewOpen(false)}
          onQueued={() => {
            setBatch([]);
            void qc.invalidateQueries({ queryKey: ['authoring'] });
          }}
        />
      )}
    </div>
  );
}
